import { z } from "zod";
import { apiClient } from "./api";

export const VALIDATION_QUEUE_QUERY_KEY = ["validation", "queue"] as const;
export const VALIDATION_ITEMS_QUERY_KEY = ["validation", "items"] as const;
export const VALIDATION_STATS_QUERY_KEY = ["validation", "stats"] as const;
export const VALIDATION_GROUPS_QUERY_KEY = ["validation", "groups"] as const;

export const knowledgeItemSourceSchema = z.enum([
  "upload",
  "chat",
  "url",
  "github",
  "notion",
  "shortcut",
  "s3",
  "postman",
  "self",
  "jira",
  "feedback",
  "story_as_built",
]);
export type KnowledgeItemSource = z.infer<typeof knowledgeItemSourceSchema>;

/**
 * The reviewer-facing origin label for an item's card/detail header. A merge
 * (`POST /validation/groups/merge-confirm`) stores the canonical item with
 * `source=CHAT` — deliberately, so it flows through the existing chat-learned
 * pipeline — but rendering it as plain "chat" origin hides that it is a
 * curated fusion of N Jira proposals with "Sin evaluación". Whenever
 * `mergedFromCount` is a positive lineage count, that fusion label replaces
 * the raw source; the stored `source` enum value itself is never changed.
 */
export function originLabel(source: KnowledgeItemSource, mergedFromCount: number | null): string {
  if (mergedFromCount !== null && mergedFromCount > 0) {
    return `Fusionado (${mergedFromCount} orígenes)`;
  }
  if (source === "feedback") {
    return "Feedback";
  }
  if (source === "story_as_built") {
    return "Implementación real";
  }
  return source;
}

export const knowledgeItemStatusSchema = z.enum(["pending", "approved", "rejected"]);
export type KnowledgeItemStatus = z.infer<typeof knowledgeItemStatusSchema>;

export const validationRelevanceOrderSchema = z.enum(["none", "high_first", "low_first"]);
export type ValidationRelevanceOrder = z.infer<typeof validationRelevanceOrderSchema>;

export const validationDateOrderSchema = z.enum(["none", "newest_first", "oldest_first"]);
export type ValidationDateOrder = z.infer<typeof validationDateOrderSchema>;

export const validationRelevanceFilterSchema = z.enum(["high", "medium", "low", "unassessed"]);
export type ValidationRelevanceFilter = z.infer<typeof validationRelevanceFilterSchema>;

const assessmentStatusSchema = z.enum(["queued", "running", "completed", "failed"]);
const relevanceMarkSchema = z.enum(["high", "medium", "low"]);
const assessmentTriggerSchema = z.enum(["created", "requeued", "backfill", "manual"]);
const proposalQualitySchema = z.enum(["high", "medium", "low"]);
const sourceSupportSchema = z.enum(["supported", "partial", "unsupported", "not_evaluable"]);
const domainRelevanceSchema = z.enum(["high", "medium", "low"]);
const corpusContributionSchema = z.enum([
  "new",
  "complementary",
  "duplicate",
  "contradictory",
  "uncertain",
]);
const assessmentConfidenceSchema = z.enum(["high", "medium", "low"]);
const knowledgeReusabilitySchema = z.enum(["high", "medium", "low"]);
const operationalNatureSchema = z.enum([
  "reusable",
  "routine_one_off",
  "case_specific",
  "uncertain",
]);
const actionabilitySchema = z.enum(["high", "medium", "low"]);
const incidentCriticalitySchema = z.enum(["critical", "non_critical", "uncertain"]);
const criticalityEvidenceSchema = z.enum([
  "explicit_metadata",
  "concrete_impact",
  "both",
  "insufficient",
]);
const reviewReasonSchema = z.enum([
  "reusable_knowledge",
  "critical_incident",
  "rejected_ticket_capped",
]);
const assessmentTimestampSchema = z.string().datetime({ offset: true });
const dimensionSignalDirectionSchema = z.enum(["up", "down", "neutral"]);
const dimensionSignalsSchema = z
  .object({
    quality: dimensionSignalDirectionSchema,
    source_support: dimensionSignalDirectionSchema,
    domain_relevance: dimensionSignalDirectionSchema,
    corpus_contribution: dimensionSignalDirectionSchema,
    confidence: dimensionSignalDirectionSchema,
    knowledge_reusability: dimensionSignalDirectionSchema,
    operational_nature: dimensionSignalDirectionSchema,
    actionability: dimensionSignalDirectionSchema,
    incident_criticality: dimensionSignalDirectionSchema,
    criticality_evidence: dimensionSignalDirectionSchema,
  })
  .strict();
export type DimensionSignals = z.infer<typeof dimensionSignalsSchema>;
export type DimensionSignalDirection = z.infer<typeof dimensionSignalDirectionSchema>;
const relevanceScoreSchema = z.number().int().min(0).max(100);

const noAssessmentErrorShape = {
  error_code: z.null(),
  error_message: z.null(),
};
const failedAssessmentErrorShape = {
  error_code: z.string().trim().min(1).max(64),
  error_message: z.string().trim().min(1).max(500),
};
const completedAssessmentSummaryBranch = z
  .object({
    status: z.literal("completed"),
    relevance_mark: relevanceMarkSchema,
    updating: z.literal(false),
    retryable: z.literal(false),
    assessed_at: assessmentTimestampSchema,
    ...noAssessmentErrorShape,
  })
  .strict();
const queuedAssessmentSummaryBranch = z
  .object({
    status: z.literal("queued"),
    relevance_mark: relevanceMarkSchema.nullable(),
    updating: z.boolean(),
    retryable: z.literal(false),
    assessed_at: assessmentTimestampSchema.nullable(),
    ...noAssessmentErrorShape,
  })
  .strict();
const runningAssessmentSummaryBranch = z
  .object({
    status: z.literal("running"),
    relevance_mark: relevanceMarkSchema.nullable(),
    updating: z.boolean(),
    retryable: z.literal(false),
    assessed_at: assessmentTimestampSchema.nullable(),
    ...noAssessmentErrorShape,
  })
  .strict();
const failedAssessmentSummaryBranch = z
  .object({
    status: z.literal("failed"),
    relevance_mark: relevanceMarkSchema.nullable(),
    updating: z.literal(false),
    retryable: z.literal(true),
    assessed_at: assessmentTimestampSchema.nullable(),
    ...failedAssessmentErrorShape,
  })
  .strict();

type AssessmentLifecycleProjection = {
  status: z.infer<typeof assessmentStatusSchema>;
  relevance_mark: z.infer<typeof relevanceMarkSchema> | null;
  updating: boolean;
  assessed_at: string | null;
};

function validateAssessmentLifecycle(
  value: AssessmentLifecycleProjection,
  context: z.RefinementCtx,
): void {
  const hasResult = value.relevance_mark !== null;
  if (hasResult !== (value.assessed_at !== null)) {
    context.addIssue({
      code: "custom",
      path: ["assessed_at"],
      message: "assessment mark and timestamp must be present together",
    });
  }
  if ((value.status === "queued" || value.status === "running") && value.updating !== hasResult) {
    context.addIssue({
      code: "custom",
      path: ["updating"],
      message: "active updating must reflect whether a previous result exists",
    });
  }
}

const assessmentSummarySchema = z
  .discriminatedUnion("status", [
    completedAssessmentSummaryBranch,
    queuedAssessmentSummaryBranch,
    runningAssessmentSummaryBranch,
    failedAssessmentSummaryBranch,
  ])
  .superRefine(validateAssessmentLifecycle);
export type AssessmentSummary = z.infer<typeof assessmentSummarySchema>;

const relatedKnowledgeSchema = z
  .object({
    knowledge_item_id: z.guid(),
    source: knowledgeItemSourceSchema.nullable(),
    similarity: z.number().min(0).max(1),
    excerpt: z.string(),
    verdict: z.enum(["duplicate", "contradictory", "complementary", "unrelated"]).nullable(),
    confidence: z.number().min(0).max(1).nullable(),
  })
  .strict();

const assessmentDetailCommonShape = {
  attempt_id: z.guid().nullable(),
  trigger: assessmentTriggerSchema.nullable(),
  rubric_version: z.string().nullable(),
  schema_revision: z.string().nullable(),
  policy_fingerprint: z.string().length(64).nullable(),
  origin_evidence: z.tuple([]),
};
const nullableAssessmentDetailResultShape = {
  quality: proposalQualitySchema.nullable(),
  source_support: sourceSupportSchema.nullable(),
  domain_relevance: domainRelevanceSchema.nullable(),
  corpus_contribution: corpusContributionSchema.nullable(),
  confidence: assessmentConfidenceSchema.nullable(),
  knowledge_reusability: knowledgeReusabilitySchema.nullable(),
  operational_nature: operationalNatureSchema.nullable(),
  actionability: actionabilitySchema.nullable(),
  incident_criticality: incidentCriticalitySchema.nullable(),
  criticality_evidence: criticalityEvidenceSchema.nullable(),
  review_reason: reviewReasonSchema.nullable(),
  summary_es: z.string().min(1).nullable(),
  rationale_es: z.string().min(1).nullable(),
  related_knowledge: z.array(relatedKnowledgeSchema),
  effective_provider: z.string().min(1).nullable(),
  effective_model: z.string().min(1).nullable(),
  relevance_score: relevanceScoreSchema.nullable(),
  dimension_signals: dimensionSignalsSchema.nullable(),
};
const assessmentDetailWithResultShape = {
  quality: proposalQualitySchema,
  source_support: sourceSupportSchema,
  domain_relevance: domainRelevanceSchema,
  corpus_contribution: corpusContributionSchema,
  confidence: assessmentConfidenceSchema,
  knowledge_reusability: knowledgeReusabilitySchema,
  operational_nature: operationalNatureSchema,
  actionability: actionabilitySchema,
  incident_criticality: incidentCriticalitySchema,
  criticality_evidence: criticalityEvidenceSchema,
  review_reason: reviewReasonSchema.nullable(),
  summary_es: z.string().min(1),
  rationale_es: z.string().min(1),
  related_knowledge: z.array(relatedKnowledgeSchema),
  effective_provider: z.string().min(1),
  effective_model: z.string().min(1),
  relevance_score: relevanceScoreSchema,
  dimension_signals: dimensionSignalsSchema,
};
const assessmentDetailSchema = z
  .discriminatedUnion("status", [
    completedAssessmentSummaryBranch.extend({
      ...assessmentDetailCommonShape,
      ...assessmentDetailWithResultShape,
    }),
    queuedAssessmentSummaryBranch.extend({
      ...assessmentDetailCommonShape,
      ...nullableAssessmentDetailResultShape,
    }),
    runningAssessmentSummaryBranch.extend({
      ...assessmentDetailCommonShape,
      ...nullableAssessmentDetailResultShape,
    }),
    failedAssessmentSummaryBranch.extend({
      ...assessmentDetailCommonShape,
      ...nullableAssessmentDetailResultShape,
    }),
  ])
  .superRefine((value, context) => {
    validateAssessmentLifecycle(value, context);
    const hasResult = value.relevance_mark !== null;
    const resultValues = [
      value.quality,
      value.source_support,
      value.domain_relevance,
      value.corpus_contribution,
      value.confidence,
      value.knowledge_reusability,
      value.operational_nature,
      value.actionability,
      value.incident_criticality,
      value.criticality_evidence,
      value.summary_es,
      value.rationale_es,
      value.effective_provider,
      value.effective_model,
      value.relevance_score,
      value.dimension_signals,
    ];
    const resultIsComplete = resultValues.every((resultValue) => resultValue !== null);
    const resultIsEmpty =
      resultValues.every((resultValue) => resultValue === null) &&
      value.related_knowledge.length === 0;
    if ((hasResult && !resultIsComplete) || (!hasResult && !resultIsEmpty)) {
      context.addIssue({
        code: "custom",
        path: ["relevance_mark"],
        message: "assessment result fields must match relevance mark presence",
      });
    }
    // `reusable_knowledge` / `critical_incident` are the judge's own review
    // reasons and only ever accompany a `high` mark. `rejected_ticket_capped`
    // is the deterministic rejection-disposition cap (see
    // `derive_relevance_decision` on the backend): it forces the mark down to
    // `medium`, so it can never co-occur with `high` and every other mark
    // must carry no review reason at all.
    const reviewReasonRequiresHigh =
      value.review_reason === "reusable_knowledge" || value.review_reason === "critical_incident";
    const reviewReasonRequiresMedium = value.review_reason === "rejected_ticket_capped";
    if (reviewReasonRequiresHigh && value.relevance_mark !== "high") {
      context.addIssue({
        code: "custom",
        path: ["review_reason"],
        message: "reusable_knowledge/critical_incident review reasons require a high mark",
      });
    }
    if (reviewReasonRequiresMedium && value.relevance_mark !== "medium") {
      context.addIssue({
        code: "custom",
        path: ["review_reason"],
        message: "rejected_ticket_capped requires the capped medium mark",
      });
    }
    if (value.relevance_mark === "high" && value.review_reason === null) {
      context.addIssue({
        code: "custom",
        path: ["review_reason"],
        message: "a high mark requires a review reason",
      });
    }
  });
export type AssessmentDetail = z.infer<typeof assessmentDetailSchema>;

const pendingItemSchema = z.object({
  id: z.guid(),
  source: knowledgeItemSourceSchema,
  source_ref: z.string().nullable(),
  submitted_by: z.guid(),
  created_at: z.string(),
  preview: z.string(),
});
export type PendingItem = z.infer<typeof pendingItemSchema>;

// `origin_rejection_disposition` / `merged_from_count` default when absent so
// older cached fixtures/mocks (and any transitional backend response) don't
// fail to parse; the inferred TS type still requires both fields on every
// value that flows through the app, matching the always-present contract.
const validationItemSummarySchema = pendingItemSchema.extend({
  status: knowledgeItemStatusSchema,
  assessment: assessmentSummarySchema.nullish(),
  origin_rejection_disposition: z.boolean().default(false),
  merged_from_count: z.number().int().nonnegative().nullable().default(null),
});
export type ValidationItemSummary = z.infer<typeof validationItemSummarySchema>;

const queueResponseSchema = z.object({
  items: z.array(pendingItemSchema),
});

const validationItemsResponseSchema = z.object({
  items: z.array(validationItemSummarySchema),
});

const validationBackfillProgressSchema = z
  .object({
    eligible: z.number().int().nonnegative(),
    completed_high: z.number().int().nonnegative(),
    completed_medium: z.number().int().nonnegative(),
    completed_low: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    active: z.number().int().nonnegative(),
    uncovered: z.number().int().nonnegative(),
    complete: z.boolean(),
  })
  .strict();
const pendingCountsByRelevanceSchema = z
  .object({
    high: z.number().int().nonnegative(),
    medium: z.number().int().nonnegative(),
    low: z.number().int().nonnegative(),
    unassessed: z.number().int().nonnegative(),
  })
  .strict();
export type PendingCountsByRelevance = z.infer<typeof pendingCountsByRelevanceSchema>;

const validationStatsSchema = z
  .object({
    pending_count: z.number().int().nonnegative(),
    high_relevance_pending_count: z.number().int().nonnegative(),
    relevance_backfill: validationBackfillProgressSchema,
    pending_counts_by_relevance: pendingCountsByRelevanceSchema,
  })
  .strict();
export type ValidationStats = z.infer<typeof validationStatsSchema>;

const chatContextMessageSchema = z.object({
  id: z.guid(),
  role: z.string(),
  content: z.string(),
  created_at: z.string(),
});
export type ChatContextMessage = z.infer<typeof chatContextMessageSchema>;

// The `as_built` block of a `story_as_built` item (`record-story-as-built-knowledge`): what a
// reviewer needs beside the proposal. The serving model and the confidence come from the item's
// operator-only metadata. Every nullable field is null (and every list empty) when the item's
// metadata does not record it. The objects are not strict: a field added to the block later must
// not blank the reviewer's detail.
const asBuiltModelSchema = z.object({
  provider: z.string(),
  model: z.string(),
  served_via_fallback: z.boolean(),
});
const asBuiltSupersedesSchema = z.object({
  item_id: z.guid(),
  // The replaced item's content, for the diff; null when that item can no longer be read.
  content: z.string().nullable(),
});
const asBuiltRelatedSchema = z.object({
  item_id: z.guid(),
  story_id: z.number().int().nonnegative(),
  // The story title read from that item's heading; null when it cannot be read.
  title: z.string().nullable(),
});
const asBuiltDetailSchema = z.object({
  story_public_id: z.number().int().nonnegative().nullable(),
  story_url: z.string().nullable(),
  // `repo#123` or `repo@sha7`.
  pull_requests: z.array(z.string()),
  confidence: z.number().nullable(),
  model: asBuiltModelSchema.nullable(),
  // `YYYY-MM-DD`, the first and the last merge to `prod`.
  prod_merged_from: z.string().nullable(),
  prod_merged_to: z.string().nullable(),
  supersedes: asBuiltSupersedesSchema.nullable(),
  related: z.array(asBuiltRelatedSchema),
});
export type AsBuiltDetail = z.infer<typeof asBuiltDetailSchema>;

const itemDetailSchema = z.object({
  id: z.guid(),
  source: knowledgeItemSourceSchema,
  source_ref: z.string().nullable(),
  raw_content: z.string(),
  status: knowledgeItemStatusSchema,
  submitted_by: z.guid(),
  created_at: z.string(),
  validated_by: z.guid().nullable(),
  validated_at: z.string().nullable(),
  rejection_reason: z.string().nullable(),
  external_url: z.string().nullable(),
  originating_session_id: z.guid().nullable(),
  recent_messages: z.array(chatContextMessageSchema),
  assessment: assessmentDetailSchema.nullish(),
  origin_rejection_disposition: z.boolean().default(false),
  // "provider/model" that synthesised a Jira proposal; null for other sources
  // and for proposals created before the model was recorded.
  origin_synthesis_model: z.string().nullable().default(null),
  origin_synthesis_via_fallback: z.boolean().nullable().default(null),
  merged_from_count: z.number().int().nonnegative().nullable().default(null),
  // Null for every source but `story_as_built`.
  as_built: asBuiltDetailSchema.nullable().default(null),
});
export type ItemDetail = z.infer<typeof itemDetailSchema>;

const actionResponseSchema = z.object({
  knowledge_item_id: z.guid(),
  status: knowledgeItemStatusSchema,
});

const requeueResponseSchema = z.object({
  knowledge_item_id: z.guid(),
  status: z.literal("pending"),
  previous_status: z.enum(["approved", "rejected"]),
});
export type RequeueResponse = z.infer<typeof requeueResponseSchema>;

const assessmentRetryResponseSchema = z.object({
  knowledge_item_id: z.guid(),
  assessment_id: z.guid(),
  status: assessmentStatusSchema,
});
export type AssessmentRetryResponse = z.infer<typeof assessmentRetryResponseSchema>;

export async function listPendingQueue(options?: {
  limit?: number;
  offset?: number;
}): Promise<PendingItem[]> {
  const result = await apiClient.get("/validation/queue", queueResponseSchema, {
    query: { limit: options?.limit, offset: options?.offset },
  });
  return result.items;
}

export async function listValidationItems(options: {
  status: KnowledgeItemStatus;
  query?: string;
  relevance?: ReadonlyArray<ValidationRelevanceFilter>;
  relevanceOrder?: ValidationRelevanceOrder;
  dateOrder?: ValidationDateOrder;
  limit?: number;
  offset?: number;
}): Promise<ValidationItemSummary[]> {
  const query = options.query?.trim();
  const result = await apiClient.get("/validation/items", validationItemsResponseSchema, {
    query: {
      status: options.status,
      q: query || undefined,
      relevance: options.relevance,
      relevance_order: options.relevanceOrder,
      date_order: options.dateOrder,
      limit: options.limit,
      offset: options.offset,
    },
  });
  return result.items;
}

export function getValidationStats(): Promise<ValidationStats> {
  return apiClient.get("/validation/stats", validationStatsSchema);
}

export async function getItemDetail(id: string): Promise<ItemDetail> {
  return apiClient.get(`/validation/items/${id}`, itemDetailSchema);
}

export async function approveItem(id: string): Promise<void> {
  await apiClient.post(`/validation/items/${id}/approve`, actionResponseSchema, {});
}

export async function rejectItem(id: string, reason: string): Promise<void> {
  await apiClient.post(`/validation/items/${id}/reject`, actionResponseSchema, { reason });
}

export function requeueItem(id: string): Promise<RequeueResponse> {
  return apiClient.post(`/validation/items/${id}/requeue`, requeueResponseSchema, {});
}

export function retryItemAssessment(id: string): Promise<AssessmentRetryResponse> {
  return apiClient.post(
    `/validation/items/${id}/assessment/retry`,
    assessmentRetryResponseSchema,
    {},
  );
}

// --- Pending proposal groups (resolve-pending-proposal-groups) -----------
//
// NOTE: these schemas are derived from
// `openspec/changes/resolve-pending-proposal-groups/specs/validation/spec.md`
// and `design.md` D1/D2/D3/D4.

export const relationshipVerdictSchema = z.enum(["duplicate", "complementary"]);
export type RelationshipVerdict = z.infer<typeof relationshipVerdictSchema>;

/**
 * The two stale-group 409 `detail.code` variants (see `POST
 * /validation/groups/merge-draft|merge-confirm` and `POST
 * /validation/items/{id}/resolve-group`): a referenced member is no longer
 * covered by the current group (`group_membership_changed`) or the caller's
 * `expected_group_hash` no longer matches current state
 * (`group_hash_mismatch`). Both carry a Spanish `detail.message`; the UI
 * matches on `detail.code` (falling back to plain HTTP 409 for older/opaque
 * error shapes) to decide when to refetch the groups panel automatically.
 */
export const GROUP_CONFLICT_ERROR_CODES = [
  "group_membership_changed",
  "group_hash_mismatch",
] as const;

// `recommended_reason` is a plain, already-human Spanish string built
// server-side (`_recommended_reason_text` in `backend/src/api/validation.py`)
// from the pure `recommend_keeper` dimension decision — the frontend never
// re-derives or translates it, just displays it verbatim.

const groupMemberSchema = z
  .object({
    id: z.guid(),
    created_at: z.string(),
    preview: z.string(),
  })
  .strict();
export type GroupMember = z.infer<typeof groupMemberSchema>;

const groupEdgeSchema = z
  .object({
    member_a: z.guid(),
    member_b: z.guid(),
    verdict: relationshipVerdictSchema,
    confidence: z.number().min(0).max(1),
  })
  .strict();
export type GroupEdge = z.infer<typeof groupEdgeSchema>;

const proposalGroupSchema = z
  .object({
    group_hash: z.string().min(1),
    members: z.array(groupMemberSchema).min(1),
    edges: z.array(groupEdgeSchema),
    recommended_id: z.guid(),
    recommended_reason: z.string().min(1),
    truncated: z.boolean(),
    true_member_count: z.number().int().positive(),
  })
  .strict();
export type ProposalGroup = z.infer<typeof proposalGroupSchema>;

const groupsResponseSchema = z
  .object({
    groups: z.array(proposalGroupSchema),
  })
  .strict();

const groupItemOutcomeSchema = z.enum(["rejected", "conflict", "skipped_complementary"]);
export type GroupItemOutcome = z.infer<typeof groupItemOutcomeSchema>;

const groupItemResultSchema = z
  .object({
    item_id: z.guid(),
    outcome: groupItemOutcomeSchema,
  })
  .strict();
export type GroupItemResult = z.infer<typeof groupItemResultSchema>;

const resolveGroupResponseSchema = z
  .object({
    keep_id: z.guid(),
    results: z.array(groupItemResultSchema),
  })
  .strict();
export type ResolveGroupResponse = z.infer<typeof resolveGroupResponseSchema>;

const mergeDraftResponseSchema = z
  .object({
    final_title: z.string().min(1),
    final_text: z.string().min(1),
  })
  .strict();
export type MergeDraft = z.infer<typeof mergeDraftResponseSchema>;

const mergeConfirmResponseSchema = z
  .object({
    knowledge_item_id: z.guid(),
    status: z.literal("approved"),
    results: z.array(groupItemResultSchema),
  })
  .strict();
export type MergeConfirmResponse = z.infer<typeof mergeConfirmResponseSchema>;

export async function listValidationGroups(): Promise<ProposalGroup[]> {
  const result = await apiClient.get("/validation/groups", groupsResponseSchema);
  return result.groups;
}

export function resolveGroup(
  keepId: string,
  options: { rejectIds: readonly string[]; expectedGroupHash: string },
): Promise<ResolveGroupResponse> {
  return apiClient.post(`/validation/items/${keepId}/resolve-group`, resolveGroupResponseSchema, {
    reject_ids: options.rejectIds,
    expected_group_hash: options.expectedGroupHash,
  });
}

export function mergeDraft(memberIds: readonly string[]): Promise<MergeDraft> {
  return apiClient.post("/validation/groups/merge-draft", mergeDraftResponseSchema, {
    member_ids: memberIds,
  });
}

export function mergeConfirm(options: {
  memberIds: readonly string[];
  finalTitle: string;
  finalText: string;
  expectedGroupHash: string;
}): Promise<MergeConfirmResponse> {
  return apiClient.post("/validation/groups/merge-confirm", mergeConfirmResponseSchema, {
    member_ids: options.memberIds,
    final_title: options.finalTitle,
    final_text: options.finalText,
    expected_group_hash: options.expectedGroupHash,
  });
}

const ignoreGroupResponseSchema = z
  .object({
    group_hash: z.string().min(1),
    ignored: z.literal(true),
  })
  .strict();
export type IgnoreGroupResponse = z.infer<typeof ignoreGroupResponseSchema>;

export function ignoreGroup(options: {
  expectedGroupHash: string;
}): Promise<IgnoreGroupResponse> {
  return apiClient.post("/validation/groups/ignore", ignoreGroupResponseSchema, {
    expected_group_hash: options.expectedGroupHash,
  });
}
